/*
 * <jj-couch-stepper>
 *
 * Lets a customer add paid inflatable couches to a package from the package's
 * own product page, and keeps the total honest against a finite physical pool
 * of ten couches shared by every package in the cart.
 *
 * WHY THIS EXISTS RATHER THAN A LINE ITEM PROPERTY
 * ------------------------------------------------
 * An extra couch is a real $25 line, not a note on the package line. That
 * means two variants have to reach the cart from one Add to Event, and
 * assets/product-form.js posts a single FormData with a single id. So when
 * the customer has asked for at least one couch this element intercepts the
 * submit and posts one multi-item /cart/add.js call instead.
 *
 * WHY IT DOES NOT TOUCH product-form.js
 * -------------------------------------
 * That file is shared by every product in the store - tents, bounce houses,
 * Nerf, flower walls - and it already carries scar tissue from a bug that
 * added the wrong variant. This element is loaded only by
 * snippets/jj-couch-stepper.liquid, which is rendered only by the Movie Night
 * template, and it listens on document in the CAPTURE phase. product-form.js
 * binds on the form itself in the bubble phase, so capture always runs first
 * and stopImmediatePropagation() keeps the event away from it entirely.
 *
 * WHEN NOTHING EXTRA IS ASKED FOR NOTHING IS INTERCEPTED
 * ------------------------------------------------------
 * The handler returns immediately and the stock Spotlight submit runs exactly
 * as it does on every other product. The package still gets its _line_key and
 * _included_couches, because those are plain hidden inputs carrying a form=
 * attribute - the same trick snippets/jj-concession-picker.liquid uses. This
 * keeps the common path free of any duplicated cart code.
 *
 * CART UI
 * -------
 * The multi-item call requests the same rendered sections that
 * product-form.js asks for and hands the response to the same
 * cart.renderContents(), so the cart notification and the header badge behave
 * identically to a normal add. Atomicity is not traded for a broken cart.
 *
 * ------------------------------------------------------------------------
 * 2026-08-23 (variant change fix): the included couch count was read from the
 * form's hidden input[name="id"] as soon as PUB_SUB_EVENTS.variantChange
 * fired. <product-info> rewrites that input as part of swapping in freshly
 * fetched section markup, and on this theme it had not always done so by the
 * time the subscriber ran - not even one animation frame later. Switching
 * Basic to Standard therefore recomputed the cap against Basic and left the
 * stepper offering ten couches on a package that already includes four.
 *
 * The published variant is now taken from the event payload, which is correct
 * the instant the event fires, and the DOM is used only as a fallback. A
 * delayed third pass clears the payload override and re-syncs from the settled
 * DOM, so an unavailable variant or a payload without a variant still lands on
 * the right number.
 * ------------------------------------------------------------------------
 * 2026-08-29 (second concession): this element is now the SINGLE submitter for
 * every extra line on the page, not just couches.
 * Backup of the previous revision: assets/jj-backup-20260829b-jj-couch-stepper.js
 *
 * The $60 second concession also has to reach the cart as a real line. Two
 * capture-phase interceptors on one form would fight over
 * stopImmediatePropagation() and could produce two non-atomic adds, so instead
 * <jj-concession-picker> registers itself with window.JJCartExtras and this
 * element folds those lines into the single call it already makes. See
 * assets/jj-cart-extras.js.
 *
 * Four changes, all inside onSubmit(), and nothing else in this file moved:
 *
 *   1. it now intercepts when couches > 0 OR the registry has lines pending
 *   2. it asks the registry to validate before doing anything irreversible
 *   3. the couch line is only pushed when couches > 0 - previously it was
 *      unconditional, because the only reason to intercept was couches
 *   4. the registry is reset after a successful add
 *
 * Every lookup is guarded, so if assets/jj-cart-extras.js is missing or no
 * provider ever registers, the behaviour is byte-for-byte what it was before.
 * The Drive-In page renders this stepper with no concession picker, so it has
 * no providers and is completely unaffected.
 *
 * A latent bug is closed as a side effect. This element registers its document
 * listener before the concession picker registers its own, so whenever couches
 * were involved the picker's "you must choose a machine" guard was skipped by
 * stopImmediatePropagation(). Routing that guard through the registry means it
 * now runs on both paths.
 * ------------------------------------------------------------------------
 *
 * ENFORCEMENT
 * -----------
 * None of this is enforcement. The stepper is a UX limit. The authoritative
 * pool arithmetic is recomputed in Liquid from the real cart by
 * snippets/rental-resource-pool.liquid when the Event Request is rendered.
 */

(function () {
  'use strict';

  if (customElements.get('jj-couch-stepper')) return;

  var POOL_TOTAL_FALLBACK = 10;
  var VARIANT_SETTLE_MS = 400;

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* The shared extra-line registry, or null when it never loaded. */
  function extrasRegistry() {
    return window.JJCartExtras || null;
  }

  /*
   * Unique per add. This is what stops Shopify merging two adds of the same
   * package variant into one line of quantity 2, which would destroy the
   * per-package couch attribution.
   */
  function makeLineKey() {
    return 'mn-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  customElements.define(
    'jj-couch-stepper',
    class JjCouchStepper extends HTMLElement {
      connectedCallback() {
        this.submitting = false;
        this.pendingVariantId = '';

        this.total = toInt(this.dataset.total, POOL_TOTAL_FALLBACK);
        this.addonVariantId = toInt(this.dataset.addonVariant, 0);
        this.usedInCart = toInt(this.dataset.used, 0);
        this.productTitle = this.dataset.productTitle || '';

        this.form = document.getElementById(this.dataset.formId || '');
        this.qtyInput = this.querySelector('[data-cs-qty]');
        this.statusEl = this.querySelector('[data-cs-status]');
        this.errorEl = this.querySelector('[data-cs-error]');
        this.lineKeyInput = this.querySelector('[data-cs-line-key]');
        this.includedInput = this.querySelector('[data-cs-included]');

        this.variantMap = {};
        var mapNode = this.querySelector('[data-cs-variant-map]');
        if (mapNode) {
          try {
            this.variantMap = JSON.parse(mapNode.textContent);
          } catch (error) {
            console.error('Couch stepper: variant map unreadable', error);
          }
        }

        /*
         * Anything missing means the page cannot be trusted to build a correct
         * two-line add. Hide the stepper and leave the stock form alone rather
         * than half-working.
         */
        if (!this.form || !this.qtyInput || !this.addonVariantId) {
          console.error('Couch stepper: form, quantity input or add-on variant missing. Stepper disabled.');
          this.hidden = true;
          return;
        }

        this.cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindControls();
        this.subscribeVariantChange();
        this.syncVariant();
        this.refreshFromCart();

        this.boundSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.boundSubmit, true);
      }

      disconnectedCallback() {
        if (this.boundSubmit) document.removeEventListener('submit', this.boundSubmit, true);
        if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
        if (this.settleTimer) window.clearTimeout(this.settleTimer);
      }

      /* ----------------------------------------------------------------
         State
         ---------------------------------------------------------------- */

      /*
       * The variant the customer is actually looking at. The event payload
       * wins while a variant change is still settling, because the hidden
       * input is rewritten by <product-info> on its own schedule.
       */
      get currentVariantId() {
        if (this.pendingVariantId) return this.pendingVariantId;
        var input = this.form.querySelector('[name="id"]');
        return input ? String(input.value) : '';
      }

      get currentVariant() {
        return this.variantMap[this.currentVariantId] || null;
      }

      get includedForCurrentVariant() {
        var variant = this.currentVariant;
        return variant ? toInt(variant.included, 0) : 0;
      }

      /*
       * Couches this package could still add, given everything already in the
       * cart plus what this package brings with it. Clamped at zero for the
       * control; the raw negative lives in the Liquid pool snippet, which is
       * what decides manual review.
       */
      get maxExtra() {
        return Math.max(0, this.total - this.usedInCart - this.includedForCurrentVariant);
      }

      get quantity() {
        return Math.max(0, toInt(this.qtyInput.value, 0));
      }

      get submitButton() {
        return this.form.querySelector('[type="submit"]');
      }

      attributionLabel() {
        var variant = this.currentVariant;
        if (variant && variant.title && variant.title !== 'Default Title') {
          return this.productTitle + ' — ' + variant.title;
        }
        return this.productTitle;
      }

      /* ----------------------------------------------------------------
         Controls
         ---------------------------------------------------------------- */

      bindControls() {
        var self = this;

        Array.prototype.slice.call(this.querySelectorAll('[data-cs-step]')).forEach(function (button) {
          button.addEventListener('click', function () {
            self.setQuantity(self.quantity + toInt(button.dataset.csStep, 0));
          });
        });

        this.qtyInput.addEventListener('change', function () {
          self.setQuantity(self.quantity);
        });

        this.qtyInput.addEventListener('input', function () {
          self.clearError();
        });
      }

      setQuantity(next) {
        this.qtyInput.value = String(Math.min(Math.max(0, next), this.maxExtra));
        this.render();
      }

      /*
       * Pushes the current variant's included count into the hidden property
       * input and re-clamps the stepper. Safe to call repeatedly.
       */
      syncVariant() {
        if (this.includedInput) this.includedInput.value = String(this.includedForCurrentVariant);
        this.setQuantity(this.quantity);
      }

      subscribeVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        var self = this;

        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var payloadVariant = event && event.data ? event.data.variant : null;
          self.pendingVariantId = payloadVariant && payloadVariant.id ? String(payloadVariant.id) : '';

          // Immediately, from the payload.
          self.syncVariant();

          // Again once <product-info> has swapped its markup in.
          window.requestAnimationFrame(function () {
            self.syncVariant();
          });

          /*
           * Finally, hand authority back to the DOM. If the payload carried no
           * variant this is the pass that gets it right.
           */
          if (self.settleTimer) window.clearTimeout(self.settleTimer);
          self.settleTimer = window.setTimeout(function () {
            self.pendingVariantId = '';
            self.syncVariant();
          }, VARIANT_SETTLE_MS);
        });
      }

      /*
       * Recomputes pool usage from the real cart. Called on load and after
       * every successful add, so a package added in another tab is accounted
       * for the next time this page touches the cart.
       */
      refreshFromCart() {
        var self = this;
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            var used = 0;

            (cart.items || []).forEach(function (item) {
              if (item.variant_id === self.addonVariantId) {
                used += item.quantity;
                return;
              }
              var included = item.properties ? item.properties._included_couches : null;
              if (included) used += toInt(included, 0) * item.quantity;
            });

            self.usedInCart = used;
            self.syncVariant();
          })
          .catch(function (error) {
            console.error('Couch stepper: could not read the cart, showing the page-load figure', error);
          });
      }

      /* ----------------------------------------------------------------
         Rendering
         ---------------------------------------------------------------- */

      render() {
        var max = this.maxExtra;
        var quantity = this.quantity;
        var included = this.includedForCurrentVariant;
        var sentences = [];

        this.qtyInput.max = String(max);

        if (included > 0) {
          sentences.push(
            included === 1
              ? '1 couch is already included with this package'
              : included + ' couches are already included with this package'
          );
        }

        if (max === 0) {
          sentences.push('No more couches are available for this event - all ' + this.total + ' are already spoken for');
        } else if (max === 1) {
          sentences.push('1 more available');
        } else {
          sentences.push(max + ' more available');
        }

        if (this.statusEl) this.statusEl.textContent = sentences.join('. ') + '.';

        Array.prototype.slice.call(this.querySelectorAll('[data-cs-step]')).forEach(function (button) {
          var step = toInt(button.dataset.csStep, 0);
          button.disabled = (step > 0 && quantity >= max) || (step < 0 && quantity <= 0);
        });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      lockButton() {
        var button = this.submitButton;
        if (!button) return;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        var spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      unlockButton() {
        var button = this.submitButton;
        if (!button) return;
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        var spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      /* ----------------------------------------------------------------
         Submission
         ---------------------------------------------------------------- */

      /*
       * Reads the product form rather than reconstructing it, so anything else
       * attached to the form - the concession picker's
       * properties[Concession Choice] in particular - travels with the package
       * line without this file knowing it exists. Disabled inputs are excluded
       * by FormData, which is exactly the behaviour the concession picker
       * relies on for non trigger variants.
       */
      collectPackagePayload() {
        var data = new FormData(this.form);
        var properties = {};

        data.forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (!match) return;
          if (String(value).trim() === '') return;
          properties[match[1]] = value;
        });

        return {
          id: data.get('id'),
          quantity: toInt(data.get('quantity'), 1),
          properties: properties
        };
      }

      onSubmit(event) {
        if (event.target !== this.form) return;

        var registry = extrasRegistry();
        var extraCount = registry ? registry.count() : 0;

        /*
         * Nothing extra asked for at all: stock behaviour, completely
         * untouched. This is still the common path.
         */
        if (this.quantity <= 0 && extraCount === 0) return;

        if (this.submitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        /*
         * Ask every provider whether the customer has finished. Done before
         * anything irreversible, and before this element swallows the event,
         * because stopImmediatePropagation() below would otherwise skip the
         * concession picker's own guard. Each provider shows its own message.
         */
        if (registry) {
          var problems = registry.validate();
          if (problems.length) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        this.submitting = true;
        this.clearError();

        var self = this;
        var lineKey = makeLineKey();

        this.lineKeyInput.value = lineKey;
        if (this.includedInput) this.includedInput.value = String(this.includedForCurrentVariant);

        var packageItem = this.collectPackagePayload();
        var couchQuantity = this.quantity;

        var items = [packageItem];

        // Only when couches were actually asked for - the interception may now
        // have been triggered by a registry line instead.
        if (couchQuantity > 0) {
          items.push({
            id: this.addonVariantId,
            quantity: couchQuantity,
            properties: {
              _package_line_key: lineKey,
              'Couches for': this.attributionLabel()
            }
          });
        }

        if (registry) {
          items = items.concat(registry.items(lineKey));
        }

        var body = { items: items };

        if (this.cartElement && typeof this.cartElement.getSectionsToRender === 'function') {
          body.sections = this.cartElement
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',');
          body.sections_url = window.location.pathname;
          if (typeof this.cartElement.setActiveElement === 'function') {
            this.cartElement.setActiveElement(document.activeElement);
          }
        }

        this.lockButton();

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (payload) {
            /*
             * Shopify returns a status field only on failure. Because the call
             * is atomic, a failure means no line was added at all, so there is
             * no half-added cart to reconcile.
             */
            if (payload.status) {
              self.showError(
                payload.description || payload.message || 'We could not add this to your event. Please try again.'
              );
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'jj-couch-stepper',
                  productVariantId: packageItem.id,
                  errors: payload.errors || payload.description,
                  message: payload.message
                });
              }
              return null;
            }

            // Paid extras are cleared so a second Add to Event cannot silently
            // charge for them again. Couches are reset just below.
            if (registry) registry.reset();

            if (!self.cartElement) {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return null;
            }

            self.cartElement.renderContents(payload);
            if (self.cartElement.classList.contains('is-empty')) self.cartElement.classList.remove('is-empty');

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'jj-couch-stepper',
                productVariantId: packageItem.id,
                cartData: payload
              });
            }

            self.setQuantity(0);
            return self.refreshFromCart();
          })
          .catch(function (error) {
            console.error('Couch stepper: add failed', error);
            self.showError('Something went wrong adding this to your event. Please try again.');
          })
          .finally(function () {
            self.submitting = false;
            self.unlockButton();
          });
      }
    }
  );
})();
